import {
  Text,
  Card,
  CardBody,
  Flex,
  Heading,
  Icon,
  Button,
  CardFooter,
  Show,
  Hide,
  IconButton,
  useToast
} from '@homebotapp/hb-react-component-catalog'
import React, { useState } from 'react'
import { ListingCompact } from '../../../api/gqlaxy/gql/generated/graphql'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { selectCustomerId } from '../../../store/selectors/customerProfile'
import { SavedSearchModel } from '../../../types/savedSearches'
import * as Sentry from '@sentry/browser'
import { selectClientId } from '../../../store/selectors/client'
import { HOME_SEARCH_SAVED_SEARCH_PAGE_DELETE } from '../../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'
import { SavedSearchAlertsCard } from './SavedSearchAlertsCard'
import { SeeAllListingsButton } from './SeeAllListingsButton'
import { SearchArea } from './SavedSearchHelpers'
import { useDeleteSavedSearch } from '../../../hooks/listings/useDeleteSavedSearch'
import { RemoveSavedSearchModal } from '../Listings/SavedSearchToggle/RemoveSavedSearchButton/RemoveSavedSearchModal'
import { SavedSearchChangedFiltersDisplay } from './SavedSearchChangedFiltersDisplay'

export interface SavedSearchCriteriaCardProps {
  savedSearch: SavedSearchModel
  listingsData: ListingCompact[]
}

const MSG = defineMessages({
  savedSearchDeleted: {
    id: 'HomeSearch.listings.savedSearch.deleted',
    defaultMessage: 'Saved search deleted'
  }
})

export const SavedSearchCriteriaCard = ({ savedSearch, listingsData }: SavedSearchCriteriaCardProps) => {
  const intl = useIntl()
  const toast = useToast()
  const customerId = useSelector(selectCustomerId)
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)
  const { mutateAsync: deleteSavedSearch } = useDeleteSavedSearch()

  const handleDelete = () => {
    if (!savedSearch?.id) {
      return
    }

    try {
      deleteSavedSearch({ deleteSavedSearchId: savedSearch?.id })
      toast({
        status: 'success',
        title: intl.formatMessage(MSG.savedSearchDeleted),
        duration: 4000
      })
    } catch (e) {
      Sentry.captureException(e)
    }
  }

  return (
    <Card
      width='100%'
      border='none'
      borderColor='none'
      boxShadow='none'
      overflow='hidden'
      backgroundColor='neutral.300'
    >
      <CardBody p={0} border='none' width='100%' borderColor='none'>
        <Flex direction='column' alignItems='flex-start'>
          <Flex width='100%' justifyContent='space-between'>
            <Heading>{savedSearch?.name}</Heading>
            <Hide above='lg'>
              <IconButton
                data-qa='delete-saved-search-mobile-icon-btn'
                aria-label='Delete saved search'
                icon={<Icon name='trash' width='12px' height='12px' />}
                variant='outline'
                size='sm'
                mb={2}
                colorScheme='danger'
                onClick={() => setDeleteModalIsOpen(true)}
                tracking={{
                  guid: '2ac4eckCakKiF5RNK8JUAn',
                  ui_context: HOME_SEARCH_SAVED_SEARCH_PAGE_DELETE,
                  descriptor: 'Delete saved search button mobile web'
                }}
              >
                <FormattedMessage id='HomeSearch.savedSearches.delete' defaultMessage='Delete' />
              </IconButton>
            </Hide>
          </Flex>
          <SearchArea firstListing={listingsData[0]} searchArea={savedSearch?.searchArea} />
          <SavedSearchChangedFiltersDisplay savedSearchOrFilter={savedSearch?.filters} />
          {/* uncomment when we wire up alert emails */}
          <SavedSearchAlertsCard savedSearch={savedSearch} />
        </Flex>
      </CardBody>

      {/* On small screen, hide this whole footer
          delete button becomes icon button and is aligned with saved search name 
          "see all 24 homes" button goes down below the listing preview pic cards (outside of this component)
      */}
      <Show above='lg'>
        <CardFooter paddingX={0} justifyContent='space-between'>
          <Button
            data-qa='delete-saved-search-desktop-icon-btn'
            leftIcon={
              <Show above='xl'>
                <Icon name='trash' width='12px' height='12px' />
              </Show>
            }
            variant='outline'
            size='sm'
            mb={2}
            colorScheme='danger'
            onClick={() => setDeleteModalIsOpen(true)}
            tracking={{
              guid: '3MMafSS9p1LPBP12TEttsE',
              ui_context: HOME_SEARCH_SAVED_SEARCH_PAGE_DELETE,
              descriptor: 'Delete saved search button'
            }}
          >
            <FormattedMessage id='HomeSearch.savedSearches.delete' defaultMessage='Delete' />
          </Button>
          <SeeAllListingsButton customerId={customerId} savedSearch={savedSearch} listingsData={listingsData} />
        </CardFooter>
      </Show>
      <RemoveSavedSearchModal
        showModal={deleteModalIsOpen}
        hideModal={() => setDeleteModalIsOpen(false)}
        deleteSavedSearch={handleDelete}
      />
    </Card>
  )
}
